import { useRef, useState, useLayoutEffect } from "react";

import { BackButton } from "@/app/ui/BackButton";
import { Image } from "@/components/Image/Image";

interface HotelGalleryProps {
  initialIndex: number;
  onBackClick: () => void;
  images: { url: string; name: string }[];
}

export function HotelGallery({
  images,
  initialIndex,
  onBackClick,
}: HotelGalleryProps) {
  const ref = useRef<HTMLUListElement>(null);

  const [index, setIndex] = useState(initialIndex);

  useLayoutEffect(() => {
    if (ref.current === null) return;

    ref.current.scrollLeft = ref.current.clientWidth * (initialIndex - 1);
  }, [initialIndex]); // initialIndex doesn't change so it's safe to have it as a dep

  const handleScroll = () => {
    if (ref.current === null) return;

    const scrollLeft = ref.current.scrollLeft;
    const newIndex =
      Math.abs(Math.round(scrollLeft / ref.current.clientWidth)) + 1;

    setIndex(newIndex);
  };

  return (
    // https://mui.com/material-ui/customization/z-index/
    // The z-index is set to be above the Whatsapp button
    <div className="bg-opacity-80 fixed top-0 left-0 z-1280 flex h-full w-full flex-col bg-black">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 p-4">
        <BackButton iconClassName="fill-white" onClick={onBackClick} />
        <div className="text-sm text-white">
          {index}/{images.length}
        </div>
        <div className="size-10" />
      </div>
      {/* Image Gallery */}
      <ul
        onScroll={handleScroll}
        ref={ref}
        className="flex h-full w-full snap-x snap-mandatory flex-nowrap overflow-scroll"
      >
        {images.map(({ url, name }, idx) => (
          <li
            key={url}
            className="relative flex h-full w-full shrink-0 grow-0 snap-center items-center justify-center p-4"
          >
            <Image
              src={url}
              alt={`${name} - Image ${idx + 1}`}
              loading="lazy"
              className="max-h-full max-w-full rounded-lg object-contain object-center"
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
